import memoize from 'memoizee';
import React from 'react';

import CK from '~/types/contentking';

import ColumnFormatter from '~/components/logic/formatters/ColumnFormatter';
import CrawlingInProgress from '~/components/logic/pagesOverview/CrawlingInProgress';
import DatatableOverlay from '~/components/patterns/tables/datatables/DatatableOverlay';
import EmptyResultOverlay from '~/components/logic/datatables/EmptyResultOverlay';
import FieldFormatter from '~/components/logic/formatters/FieldFormatter';
import FullwidthCell from '~/components/atoms/dataTables/parts/FullwidthCell';
import NewColumnHeader from '~/components/atoms/dataTables/parts/NewColumnHeader';
import ReevaluationOverlay from '~/components/app/ReevaluationOverlay';
import ResizableDatatable, {
	ResizableDatatableCellAlignment,
	type ResizableDatatableColumnDefinition,
	type ResizableDatatableContextMenuEntries,
	ResizableDatatableHeaderCellColorStyle,
	type ResizableDatatableRowGetterInput,
} from '~/components/logic/datatables/ResizableDatatable';
import SegmentLabelThumbList from '~/components/logic/segments/SegmentLabelThumbList';

import useNavigate from '~/hooks/useNavigate';

import {
	PagesColumnsAlignment,
	PagesColumnsCategory,
	getColumnAlignment,
	getColumnCategory,
	getColumnDefaultWidth,
} from '~/model/pagesColumns';

import {
	type SegmentDefinition,
	filterSegmentDefinitionsByNames,
} from '~/model/segments';

import {
	trimHostname,
} from '~/model/utils';

import matchAndReturn from '~/utilities/matchAndReturn';



const COLUMNS_WITH_FLIPPED_SORTING = [
	CK.PagesCommonColumn.AaAverageTimeSpentOnSite,
	CK.PagesCommonColumn.AaPageViews,
	CK.PagesCommonColumn.AaRevenue,
	CK.PagesCommonColumn.AaUniqueVisitors,
	CK.PagesCommonColumn.GaAverageTime,
	CK.PagesCommonColumn.GaPageValue,
	CK.PagesCommonColumn.GaPageViews,
	CK.PagesCommonColumn.GaUniquePageViews,
	CK.PagesCommonColumn.GscClicks,
	CK.PagesCommonColumn.GscCtr,
	CK.PagesCommonColumn.GscImpressions,
	CK.PagesCommonColumn.LfaBingDesktopFrequency,
	CK.PagesCommonColumn.LfaBingDesktopLastVisit,
	CK.PagesCommonColumn.LfaBingFrequency,
	CK.PagesCommonColumn.LfaBingLastVisit,
	CK.PagesCommonColumn.LfaBingMobileFrequency,
	CK.PagesCommonColumn.LfaBingMobileLastVisit,
	CK.PagesCommonColumn.LfaGoogleDesktopFrequency,
	CK.PagesCommonColumn.LfaGoogleDesktopLastVisit,
	CK.PagesCommonColumn.LfaGoogleFrequency,
	CK.PagesCommonColumn.LfaGoogleLastVisit,
	CK.PagesCommonColumn.LfaGoogleMobileFrequency,
	CK.PagesCommonColumn.LfaGoogleMobileLastVisit,
	CK.PagesCommonColumn.LfaOpenAIFrequency,
	CK.PagesCommonColumn.LfaOpenAILastVisit,
	CK.PagesCommonColumn.LfaOpenAIBotFrequency,
	CK.PagesCommonColumn.LfaOpenAIBotLastVisit,
	CK.PagesCommonColumn.LfaOpenAISearchFrequency,
	CK.PagesCommonColumn.LfaOpenAISearchLastVisit,
	CK.PagesCommonColumn.LfaOpenAIUserFrequency,
	CK.PagesCommonColumn.LfaOpenAIUserLastVisit,
	CK.PagesCommonColumn.LfaPerplexityFrequency,
	CK.PagesCommonColumn.LfaPerplexityLastVisit,
	CK.PagesCommonColumn.LfaPerplexityBotFrequency,
	CK.PagesCommonColumn.LfaPerplexityBotLastVisit,
	CK.PagesCommonColumn.LfaPerplexityUserFrequency,
	CK.PagesCommonColumn.LfaPerplexityUserLastVisit,
	CK.PagesCommonColumn.NumberOfIncomingInternalCanonicals,
	CK.PagesCommonColumn.NumberOfIncomingInternalLinks,
	CK.PagesCommonColumn.NumberOfIncomingInternalRedirects,
	CK.PagesCommonColumn.NumberOfOutgoingExternalLinks,
	CK.PagesCommonColumn.NumberOfOutgoingInternalLinks,
	CK.PagesCommonColumn.Relevance,
];

const defaultCustomColumnDefinitions = {};

const HEADER_HEIGHT = 76;
const HEADER_DISTRIBUTION_FILTER_HEIGHT = 10;
const HEADER_SEGMENT_FILTER_HEIGHT = 25;
const MAXIMUM_ROWS = 350000;



const hasData = ({ columnName, row }) => row?.[columnName] !== undefined;

const isSegmentFilterActive = memoize((columns, filter, segments) => {
	return (
		filter.has('segments')
		&& filterSegmentDefinitionsByNames(
			segments,
			[
				...filter.get('segments').get('included_in').toArray(),
				...filter.get('segments').get('not_included_in').toArray(),
			],
		).some(
			(segmentDefinition) => Object.keys(segmentDefinition.filterDefinition).some(
				(field) => columns.indexOf(field) !== -1,
			),
		)
	);
});

const isFilteredSegmentBeingReevaluated = memoize((filter, segments) => {
	return (
		filter.has('segments')
		&& filterSegmentDefinitionsByNames(
			segments,
			[
				...filter.get('segments').get('included_in').toArray(),
				...filter.get('segments').get('not_included_in').toArray(),
			],
		).some(
			(segmentDefinition) => segmentDefinition.isBeingReevaluated,
		)
	);
});

export type PagesColumnsDatatableFixedColumnWidths = Record<string, ResizableDatatableColumnDefinition<any>['width']>;



type ResizableDatatableProps = React.ComponentProps<typeof ResizableDatatable>;

type Props = {
	activeRowsLimit?: number | null,
	columns: any,
	contextMenuEntries?: ResizableDatatableContextMenuEntries<any>,
	customColumnDefinitions?: Record<string, any>,
	distributions?: any,
	filter: any,
	fixedColumnWidths?: PagesColumnsDatatableFixedColumnWidths,
	headerPropsProvider?: (input: {
		columnName: string,
	}) => Record<string, any>,
	height?: ResizableDatatableProps['height'],
	inactiveRowsFooter?: React.ReactNode,
	isColumnWidthFixed?: boolean,
	isCrawled: boolean | null,
	isDisabled?: boolean,
	isLoading: boolean,
	isRowClickable: boolean,
	isRowInactive?: (input: {
		rowIndex: number,
	}) => boolean,
	onFilterChangeCallback: (filter: any) => void,
	onLoadPagesCallback: (input: {
		rowIndex: number,
	}) => void,
	onResetFilterCallback?: any,
	onSortChangeCallback: (sortBy: any) => void,
	pagesCount: number,
	removeDefaultFilterValues: any,
	resetFilterAction: any,
	rowGetter: (input: ResizableDatatableRowGetterInput) => any,
	segmentDefinitions: ReadonlyArray<SegmentDefinition>,
	segmentsNotAllowedForFiltering?: Record<string, React.ReactNode>,
	sortBy: any,
	websiteId: CK.WebsiteId,
	width?: ResizableDatatableProps['width'],
};

const PagesColumnsDatatable: React.FC<Props> = (props) => {
	const {
		activeRowsLimit = null,
		columns,
		contextMenuEntries,
		customColumnDefinitions = defaultCustomColumnDefinitions,
		distributions,
		filter,
		fixedColumnWidths,
		headerPropsProvider,
		height,
		inactiveRowsFooter,
		isCrawled,
		isDisabled,
		isLoading,
		isRowClickable,
		isRowInactive,
		onFilterChangeCallback,
		onLoadPagesCallback,
		onResetFilterCallback,
		onSortChangeCallback,
		pagesCount,
		removeDefaultFilterValues,
		resetFilterAction,
		rowGetter,
		segmentDefinitions,
		segmentsNotAllowedForFiltering,
		sortBy,
		websiteId,
		width,
	} = props;

	const navigate = useNavigate();

	// Use refs only for values that don't need to trigger column recreation
	// Callbacks don't need to be in useMemo dependencies since they don't affect column structure
	const onFilterChangeCallbackRef = React.useRef(onFilterChangeCallback);
	const onSortChangeCallbackRef = React.useRef(onSortChangeCallback);
	const isCrawledRef = React.useRef(isCrawled);
	const isLoadingRef = React.useRef(isLoading);
	const pagesCountRef = React.useRef(pagesCount);
	const onResetFilterCallbackRef = React.useRef(onResetFilterCallback);
	const removeDefaultFilterValuesRef = React.useRef(removeDefaultFilterValues);
	const resetFilterActionRef = React.useRef(resetFilterAction);

	// Update refs synchronously during render to avoid stale values
	// Callback refs used in headerRenderer (called during render) must be updated synchronously
	// State refs used in callbacks must also be updated synchronously
	// Refs used in blankSlate (called during render) must also be updated synchronously
	onFilterChangeCallbackRef.current = onFilterChangeCallback;
	onSortChangeCallbackRef.current = onSortChangeCallback;
	isCrawledRef.current = isCrawled;
	isLoadingRef.current = isLoading;
	pagesCountRef.current = pagesCount;
	onResetFilterCallbackRef.current = onResetFilterCallback;
	removeDefaultFilterValuesRef.current = removeDefaultFilterValues;
	resetFilterActionRef.current = resetFilterAction;

	// Calculate header height - this needs current values, not refs, since it affects layout
	let calculatedHeaderHeight = HEADER_HEIGHT;

	// make table header higher when filtering on segments
	if (isSegmentFilterActive(columns, filter, segmentDefinitions)) {
		calculatedHeaderHeight += HEADER_SEGMENT_FILTER_HEIGHT;
	}

	if (distributions !== undefined) {
		calculatedHeaderHeight += HEADER_DISTRIBUTION_FILTER_HEIGHT;
	}

	const handleRowClick = React.useCallback(
		({ row }, event) => {
			navigate({
				event,
				routeName: 'website.pages.detail',
				routeParams: {
					websiteId,
					id: row.id,
				},
			});
		},
		[
			navigate,
			websiteId,
		],
	);

	const overlay = React.useCallback(
		(actualHeaderHeight) => {
			// Read from refs to avoid capturing values in closure, but values are in deps to change callback identity
			if (isLoadingRef.current || pagesCountRef.current !== 0) {
				return;
			}

			// filter and segmentDefinitions are needed for overlay logic, capture them
			if (isFilteredSegmentBeingReevaluated(filter, segmentDefinitions)) {
				return (
					<ReevaluationOverlay datatableHeaderHeight={actualHeaderHeight} />
				);
			}

			if (isCrawledRef.current === false) {
				return (
					<DatatableOverlay datatableHeaderHeight={actualHeaderHeight}>
						<CrawlingInProgress />
					</DatatableOverlay>
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			filter,
			segmentDefinitions,
			isCrawled,
			isLoading,
			pagesCount,
		], // Intentionally included to change callback identity when values change (for memoized components)
	);

	const renderBlankState = React.useCallback(
		() => {
			// Read from refs to avoid capturing values in closure, but values are in deps to change callback identity
			if (
				isFilteredSegmentBeingReevaluated(filter, segmentDefinitions)
				|| isCrawledRef.current !== true
			) {
				return;
			}

			return (
				<EmptyResultOverlay
					filter={filter}
					onResetFilterCallback={onResetFilterCallbackRef.current}
					removeDefaultValues={removeDefaultFilterValuesRef.current}
					resetAction={resetFilterActionRef.current}
				/>
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			filter,
			segmentDefinitions,
			isCrawled,
		], // Intentionally included to change callback identity when value changes (for memoized components)
	);

	// Use refs only for callbacks - they don't need to trigger column recreation
	// filter, segmentDefinitions, sortBy, distributions MUST be in useMemo deps
	// because they affect column rendering (header shows filter/sort state)

	const realColumns = React.useMemo(
		() => {
			const result = columns.toArray().map(
				(columnName) => {
					if (columnName in customColumnDefinitions) {
						return customColumnDefinitions[columnName];
					}

					const additionalOptions = headerPropsProvider
						? headerPropsProvider({ columnName })
						: {};

					const columnAlignment = getColumnAlignment(columnName);
					const columnCategory = getColumnCategory(columnName);

					const extraFields: Record<string, any> = {};

					if (fixedColumnWidths !== undefined && columnName in fixedColumnWidths) {
						extraFields.resizable = false;
						extraFields.width = fixedColumnWidths[columnName];
					}

					// Create cell renderer directly here - each closure only captures what it needs
					// CRITICAL: Only capture segmentDefinitions INSIDE the conditional for Segments column
					// This ensures non-Segments columns don't capture it in their closure Context
					const cellRenderer = (() => {
						// Only Segments column captures segmentDefinitions
						if (columnName === CK.PagesCommonColumn.Segments) {
							const capturedSegmentDefinitions = segmentDefinitions;
							return ({ innerWidth, row }) => {
								const content = (
									<SegmentLabelThumbList
										segmentDefinitions={capturedSegmentDefinitions}
										segmentNames={row[columnName]}
										width={innerWidth}
									/>
								);
								return (
									<ColumnFormatter
										column={columnName as CK.PagesColumn}
										pageType={row.type}
										value={content}
									/>
								);
							};
						}

						// All other columns - no segmentDefinitions captured
						return ({ row }) => {
							let cell;
							let content;

							if (columnName === CK.PagesCommonColumn.Url) {
								content = trimHostname(row.url);
							}

							if (content) {
								cell = (
									<ColumnFormatter
										column={columnName as CK.PagesColumn}
										pageType={row.type}
										value={content}
									/>
								);
							} else {
								cell = (
									<FieldFormatter
										column={columnName as CK.PagesColumn}
										customElements={row['custom_elements']}
										key={columnName === CK.PagesCommonColumn.Health ? row.domainId + '-' + row.id : undefined}
										pageType={row.type}
										value={row[columnName]}
									/>
								);
							}

							return cell;
						};
					})();

					// Create header renderer directly here
					// Use actual values (filter, segmentDefinitions, sortBy, distributions) since they're in useMemo deps
					// Only use refs for callbacks (onFilterChangeCallback, onSortChangeCallback) since they don't affect rendering
					const headerRenderer = ({ columnIndex, filterRef, filterWidth }) => {
						// Note: filterRef parameter is from NewColumnHeader API, not our state ref
						return (
							<NewColumnHeader
								columnCount={columns.size}
								columnIndex={columnIndex}
								columnName={columnName as CK.PagesColumn}
								columnWidth={filterWidth}
								distributions={distributions}
								filter={filter}
								isSegmentFilterActive={isSegmentFilterActive(columns, filter, segmentDefinitions)}
								key={columnName}
								onFilterChangeCallback={onFilterChangeCallbackRef.current}
								ref={filterRef}
								segmentDefinitions={segmentDefinitions}
								segmentsNotAllowedForFiltering={segmentsNotAllowedForFiltering}
								sortCallback={onSortChangeCallbackRef.current}
								sortingActive={sortBy.get('key') === columnName}
								sortingDirection={
									sortBy.get('key') === columnName
										? sortBy.get('direction')
										: null
								}
								sortingEnabled={columnName !== CK.PagesCommonColumn.Segments}
								sortingFixed={sortBy.get('fixed') ?? false}
								sortingFlipped={COLUMNS_WITH_FLIPPED_SORTING.includes(columnName as CK.PagesCommonColumn)}
								{...additionalOptions}
							/>
						);
					};

					return {
						alignment: columnAlignment === PagesColumnsAlignment.Right
							? ResizableDatatableCellAlignment.Right
							: ResizableDatatableCellAlignment.Left,
						filterName: columnName,
						headerColorStyle: matchAndReturn(columnCategory, {
							[PagesColumnsCategory.AdobeAnalytics]: ResizableDatatableHeaderCellColorStyle.AdobeAnalytics,
							[PagesColumnsCategory.Content]: ResizableDatatableHeaderCellColorStyle.Content,
							[PagesColumnsCategory.Conversions]: ResizableDatatableHeaderCellColorStyle.Conversions,
							[PagesColumnsCategory.CustomElements]: ResizableDatatableHeaderCellColorStyle.CustomElements,
							[PagesColumnsCategory.EnrichmentFields]: ResizableDatatableHeaderCellColorStyle.EnrichmentFields,
							[PagesColumnsCategory.Fundamentals]: ResizableDatatableHeaderCellColorStyle.Fundamentals,
							[PagesColumnsCategory.GoogleAnalytics]: ResizableDatatableHeaderCellColorStyle.GoogleAnalytics,
							[PagesColumnsCategory.GoogleSearchConsole]: ResizableDatatableHeaderCellColorStyle.GoogleSearchConsole,
							[PagesColumnsCategory.Indexability]: ResizableDatatableHeaderCellColorStyle.Indexability,
							[PagesColumnsCategory.Lighthouse]: ResizableDatatableHeaderCellColorStyle.Lighthouse,
							[PagesColumnsCategory.NoCategory]: ResizableDatatableHeaderCellColorStyle,
							[PagesColumnsCategory.Relations]: ResizableDatatableHeaderCellColorStyle.Relations,
							[PagesColumnsCategory.SchemaOrg]: ResizableDatatableHeaderCellColorStyle.SchemaOrg,
							[PagesColumnsCategory.SearchEngineActivity]: ResizableDatatableHeaderCellColorStyle.SearchEngineActivity,
							[PagesColumnsCategory.Social]: ResizableDatatableHeaderCellColorStyle.Social,
						}),
						hasData,
						name: columnName,
						render: {
							cell: cellRenderer,
							header: headerRenderer,
						},
						width: getColumnDefaultWidth(columnName) || 200,
						...extraFields,
					};
				},
			);
			return result;
		},
		[
			columns,
			customColumnDefinitions,
			distributions,
			filter,
			fixedColumnWidths,
			headerPropsProvider,
			segmentDefinitions,
			segmentsNotAllowedForFiltering,
			sortBy,
			// Note: onFilterChangeCallback and onSortChangeCallback are NOT in deps
			// They're accessed via refs in headerRenderer since they don't affect column structure
		],
	);

	return (
		<ResizableDatatable
			activeRowsLimit={activeRowsLimit ?? undefined}
			blankSlate={renderBlankState}
			columns={realColumns}
			contextMenuEntries={contextMenuEntries}
			filter={filter}
			headerHeight={calculatedHeaderHeight}
			height={height}
			inactiveRowsFooter={inactiveRowsFooter}
			isDisabled={isDisabled}
			isLoading={isLoading}
			isRowInactive={isRowInactive}
			lastRowNote={pagesCount > MAXIMUM_ROWS ? (
				<FullwidthCell
					limit={5000}
					rowIndex={Math.min(MAXIMUM_ROWS + 1, pagesCount) + 1}
					total={pagesCount}
				/>
			) : null}
			numberOfPinnedColumns={1}
			onFilterChange={onFilterChangeCallback}
			onRowClick={isRowClickable ? handleRowClick : undefined}
			onScroll={onLoadPagesCallback}
			overlay={overlay}
			rowGetter={rowGetter}
			rowsCount={Math.min(MAXIMUM_ROWS + 1, pagesCount)}
			width={width}
		/>
	);
};



export default PagesColumnsDatatable;
